import {
  BillingExportQuery,
  BillingTenantSettings,
  CreditNoteCreate,
  DraftLineAdd,
  Invoice,
  InvoiceList,
  Payment,
  PaymentRecord,
  Plan,
  PlanCreate,
  PlanUpdate,
  PlatformBillingList,
  Subscription,
  SubscriptionCancel,
  SubscriptionChangePlan,
  SubscriptionCreate,
  SubscriptionUpdate,
  TenantBillingView,
} from '@cloudsign/shared';
import type { InvoiceStatus, SubscriptionStatus, UsageMetric } from '@cloudsign/shared';
import { z } from 'zod';
import { t } from '../../lib/i18n';

export {
  BillingExportQuery,
  BillingTenantSettings,
  CreditNoteCreate,
  DraftLineAdd,
  Invoice,
  InvoiceList,
  Payment,
  PaymentRecord,
  Plan,
  PlanCreate,
  PlanUpdate,
  PlatformBillingList,
  Subscription,
  SubscriptionCancel,
  SubscriptionChangePlan,
  SubscriptionCreate,
  SubscriptionUpdate,
  TenantBillingView,
};

export const PLAT = '/billing/platform';
export const TENANT = '/billing';
export const PAGE_SIZE = 20;
export const PlanArray = z.array(Plan);
export const RunResult = z.object({ drafts: z.number().int() });
export const PaymentMethodUrl = z.object({ url: z.string().nullable() });

export const keys = {
  overview: (q: Record<string, unknown>) => ['billing', 'overview', q] as const,
  plans: ['billing', 'plans'] as const,
  sub: (tenantId: string) => ['billing', 'sub', tenantId] as const,
  invoices: (q: Record<string, unknown>) => ['billing', 'invoices', q] as const,
  invoice: (id: string) => ['billing', 'invoice', id] as const,
  tenant: ['billing', 'tenant'] as const,
  payUrl: ['billing', 'payment-method'] as const,
};

/** Integer pence to a pounds string, e.g. 12345 -> "£123.45" (negative: "-£1.00"). */
export function formatPounds(pence: number): string {
  const abs = Math.abs(pence);
  const text = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(abs / 100);
  return pence < 0 ? `-${text}` : text;
}

/** Pounds typed by a person ("12.5", "£1,200") to integer pence; null when it isn't a money amount. */
export function poundsToPence(text: string): number | null {
  const clean = text.trim().replace(/^£/, '').replace(/,/g, '');
  if (!/^-?\d+(\.\d{1,2})?$/.test(clean)) return null;
  return Math.round(Number(clean) * 100);
}

/** Pence to the plain number shown in a pounds input ("12.50"). */
export const penceToInput = (pence: number): string => (pence / 100).toFixed(2);

export const vatPercent = (bp: number): string => `${bp / 100}%`;

export const metricLabel = (m: UsageMetric): string => t(`billingUi.metric.${m}`);

export const INVOICE_STATUSES: InvoiceStatus[] = ['draft', 'issued', 'paid', 'credited', 'void_draft'];
export const invoiceStatusLabel = (s: InvoiceStatus): string => t(`billingUi.invoiceStatus.${s}`);
export const invoiceStatusTone = (s: InvoiceStatus): 'neutral' | 'good' | 'warn' | 'bad' =>
  s === 'paid' ? 'good' : s === 'issued' ? 'warn' : 'neutral';

export const subStatusLabel = (s: SubscriptionStatus): string => t(`billingUi.subStatus.${s}`);
export const subStatusTone = (s: SubscriptionStatus): 'neutral' | 'good' | 'warn' | 'bad' =>
  s === 'active' ? 'good' : s === 'overdue' ? 'warn' : s === 'suspended' ? 'bad' : 'neutral';

export const PAYMENT_METHODS = ['bank_transfer', 'card', 'direct_debit', 'cheque', 'other'] as const;
export const paymentMethodLabel = (m: string): string => t(`billingUi.method.${m}`);

/** Storage counters and allowances are in bytes; other metrics are plain counts. */
export function formatUsage(metric: UsageMetric, value: number): string {
  if (metric === 'storage_bytes') return `${(value / 1024 ** 3).toFixed(1)} GB`;
  return new Intl.NumberFormat('en-GB').format(value);
}
export const formatAllowance = (metric: UsageMetric, value: number | null): string =>
  value === null ? t('billingUi.unlimited') : formatUsage(metric, value);
